import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Typography,
    styled,
} from '@mui/material'
import { useUIContext } from '../../context/ui'
import CloseIcon from '@mui/icons-material/Close'
import { Colors } from '../../styles/theme'

// Faint gold-tinted divider to subtly tie drawer items to the brand
// palette (rather than MUI's default neutral gray).
const MiddleDivider = styled((props) => (
    <Divider variant="middle" {...props} />
))(() => ({
    borderColor: `rgba(185, 134, 71, 0.18)`,
}))

// ─── Nav items ────────────────────────────────────────────────────────
//
// Each item is one row in the drawer. Two link types:
//   - 'router' uses react-router's Link (stays inside the /shop SPA).
//   - 'href'   is a plain <a> (leaves the SPA, e.g. for the marketing
//              site at /, the calculator at /calc, /privacy, etc.).
//
// To add or reorder a menu item, edit this array — that's it.
const NAV_ITEMS = [
    {
        kind: 'href',
        target: '/',
        label: 'Home',
        aria: 'Go to main information page',
    },
    {
        kind: 'href',
        target: '/calc',
        label: 'Fence Materials Estimator',
        aria: 'Visit Fence Material Estimator App',
    },
    {
        kind: 'router',
        target: '/',
        label: 'Products',
        aria: 'Visit Products section',
    },
    {
        kind: 'router',
        target: '/cart',
        label: 'Cart Review',
        aria: 'Visit your cart items',
    },
    {
        kind: 'href',
        target: '/privacy',
        label: 'Privacy & Policy',
        aria: 'Read about Privacy Policy',
    },
    {
        kind: 'router',
        target: '/info/terms_conditions',
        label: 'Terms & Conditions',
        aria: 'Learn about Terms and Conditions',
    },
    {
        kind: 'router',
        target: '/info/shipping',
        label: 'Shipping Policy',
        aria: 'Learn about Shipping Policies',
    },
    {
        kind: 'router',
        target: '/info/return_policy',
        label: 'Return Policy',
        aria: 'Learn about Return Policy',
    },
    {
        kind: 'router',
        target: '/info/card_security',
        label: 'Cardholder Security Policy',
        aria: 'Learn about Cardholder Security Policy',
    },
]

export default function AppDrawer() {
    const { drawerOpen, setDrawerOpen } = useUIContext()
    const handleClose = () => setDrawerOpen(false)

    return (
        <Drawer open={drawerOpen} onClose={handleClose} anchor="left">
            {/*
              Header bar inside the drawer. Replaces the previous floating
              CloseIcon that sat outside the drawer's edge. Keeps "Menu"
              context visible for users arriving via deep link, and gives
              the close button proper visual weight + tap affordance.
            */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    // Faint gold underline matches the dividers between
                    // menu items — visually unifies the drawer.
                    borderBottom: `1px solid rgba(185, 134, 71, 0.25)`,
                }}
            >
                <Typography
                    variant="overline"
                    sx={{
                        color: Colors.secondary,
                        fontWeight: 600,
                        letterSpacing: '0.12em',
                        fontSize: '0.85rem',
                        // Same gold-ghost shadow as the menu items below.
                        textShadow: `${Colors.boltGold2} 0.5px 0.5px 0.5px`,
                    }}
                >
                    Menu
                </Typography>

                <IconButton
                    onClick={handleClose}
                    aria-label="Close menu"
                    size="small"
                    sx={{
                        color: Colors.secondary,
                        // Faint background gives tap affordance without
                        // shouting — reads as "this is a button" on hover.
                        transition:
                            'background-color 0.15s ease, color 0.15s ease',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 82, 71, 0.08)',
                            color: Colors.greenLight,
                        },
                        // Comfortable touch target.
                        minWidth: 40,
                        minHeight: 40,
                        // Pull the close button left by one button-width
                        // so it sits inset from the drawer's right edge
                        // rather than flush against it.
                        mr: 5,
                    }}
                >
                    <CloseIcon sx={{ fontSize: '1.5rem' }} />
                </IconButton>
            </Box>

            <List sx={{ pt: 0 }}>
                {NAV_ITEMS.map((item, idx) => {
                    const linkProps =
                        item.kind === 'router'
                            ? { component: RouterLink, to: item.target }
                            : { component: 'a', href: item.target }

                    return (
                        <React.Fragment key={item.label}>
                            <ListItemButton
                                {...linkProps}
                                onClick={handleClose}
                                aria-label={item.aria}
                                sx={{
                                    color: Colors.secondary,
                                    textDecoration: 'none',
                                    minHeight: 44,
                                    transition: 'background-color 0.15s ease',
                                    '&:hover': {
                                        backgroundColor:
                                            'rgba(0, 82, 71, 0.06)',
                                    },
                                }}
                            >
                                <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{
                                        sx: {
                                            // Regular weight — lighter than
                                            // the previous 500 (medium) for
                                            // a more refined, less heavy
                                            // appearance.
                                            fontWeight: 400,
                                            letterSpacing: '0.02em',
                                            textShadow: `${Colors.boltGold2} 0.5px 0.5px 0.5px`,
                                        },
                                    }}
                                />
                            </ListItemButton>
                            {idx < NAV_ITEMS.length - 1 && <MiddleDivider />}
                        </React.Fragment>
                    )
                })}
                <MiddleDivider />
            </List>
        </Drawer>
    )
}
